import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AnyObject, QueryParamsResource, ResponseList, ResponseSimple, ValueType } from '@mri/types';
import { jsonToUri } from '@mri/functions';
import { environment } from '@mri/environment';
import { AuthResourceService } from '../auth-resource';

@Injectable({
  providedIn: 'root'
})
export class GenericResourceService {
  private _apiUrl = environment.apiUrl;

  constructor(
    private _http: HttpClient,
    private _authSrcSrv: AuthResourceService
  ) {
  }

  listElements<T>(baseUri: string, query: QueryParamsResource): Observable<ResponseList<T>> {
    const { filter, paginate } = query;

    const params = [
      jsonToUri(filter || {}),
      jsonToUri(paginate as unknown as Record<string, ValueType | Array<ValueType>>, '$')
    ].filter(u => !!u.trim()).join('&');

    const url = [`${this._apiUrl}/${baseUri}/`, params].filter(u => !!u.trim()).join('?');
    return this.get<ResponseList<T>>(url);
  }

  countElements(baseUri: string, query: QueryParamsResource): Observable<ResponseList<{ _id: string }>> {
    const { filter } = query;
    const params = [
      jsonToUri(filter ?? {})
    ].filter(u => !!u.trim()).join('&');

    const url = [`${this._apiUrl}/${baseUri}/count`, params].filter(u => !!u.trim()).join('?');
    return this.get<ResponseList<{ _id: string }>>(url);
  }


  getElement<T>(baseUri: string, id?: string): Observable<ResponseSimple<T>> {
    const url = [this._apiUrl, baseUri, id].filter(v => !!v?.trim()).join('/');
    return this.get<ResponseSimple<T>>(url);
  }

  getElements<T>(baseUri: string, query: QueryParamsResource): Observable<ResponseList<T>> {
    const { filter, paginate } = query;
    const params = [
      jsonToUri(filter || {}),
      jsonToUri((paginate as unknown as Record<string, ValueType | Array<ValueType>>) || {}, '$')
    ].join('&');

    const url = [`${this._apiUrl}/${baseUri}`, params].filter(u => !!u.trim()).join('?');
    return this.get<ResponseList<T>>(url);
  }

  postElement<T>(baseUri: string, body: AnyObject): Observable<T> {
    const url = [`${this._apiUrl}/${baseUri}`].filter(u => !!u.trim()).join('?');
    return this.post<T>(url, body);
  }


  putElement<T>(baseUri: string, body: T): Observable<T> {
    const url = [`${this._apiUrl}/${baseUri}`].filter(u => !!u.trim()).join('?');
    return this.put<T>(url, body);
  }

  deleteElements<T>(baseUri: string, query: QueryParamsResource): Observable<ResponseList<T>> {
    const { filter } = query;
    const params = jsonToUri(filter || {});

    const url = [`${this._apiUrl}/${baseUri}/`, params].filter(u => !!u.trim()).join('?');
    return this.delete<ResponseList<T>>(url);
  }

  get<T = AnyObject>(url: string) {
    return this._http.get<T>(url, { headers: this._authSrcSrv.header });
  }

  post<T = AnyObject>(url: string, body: AnyObject) {
    return this._http.post<T>(url, body, { headers: this._authSrcSrv.header });
  }

  put<T = AnyObject>(url: string, body: AnyObject) {
    return this._http.put<T>(url, body, { headers: this._authSrcSrv.header });
  }

  delete<T = AnyObject>(url: string) {
    return this._http.delete<T>(url, { headers: this._authSrcSrv.header });
  }
}
